/**
 * /pages/recommendations -- renders a pre-filled recommendation box from the ?b= payload
 * and builds the cart from it (spec §8.2).
 *
 * The payload carries a box for every week in the delivery horizon, and every
 * product is fetched up front, so changing the delivery date swaps boxes with no
 * network call.
 *
 * Choosing a date here does NOT touch the cart -- the customer hasn't accepted
 * the box yet. That is why this page renders its own picker from
 * StockedDelivery.selectableDates() instead of reusing <cart-delivery-date>,
 * which commits on selection. Nothing is written until Add to cart.
 *
 * The link also carries an optional ?r=<run id>, generated by the
 * stocked-recommendations Worker per nightly run (not part of the ?b= payload
 * -- it's attribution, not cart data). It rides along as a cart attribute so
 * the resulting order can be traced back to the run that produced it.
 *
 * ABC test links also carry ?g=<group>, written as recommendations_test_group.
 * And every line added here carries a _recommended_quantity property (the
 * quantity the recommendation suggested), so the order shows which of its
 * items were the recommendation and which were added on top.
 */
(function () {
  const root = document.querySelector('[data-your-box]');
  if (!root) return;

  const els = {
    loading: root.querySelector('[data-box-loading]'),
    main: root.querySelector('[data-box-main]'),
    fallback: root.querySelector('[data-box-fallback]'),
    weeks: root.querySelector('[data-box-weeks]'),
    count: root.querySelector('[data-box-count]'),
    items: root.querySelector('[data-box-items]'),
    total: root.querySelector('[data-box-total]'),
    summaryMeta: root.querySelector('[data-box-summary-meta]'),
    cta: root.querySelector('[data-box-cta]'),
    error: root.querySelector('[data-box-error]'),
    template: document.querySelector('[data-box-item-template]'),
    dialog: document.querySelector('[data-box-cart-dialog]'),
  };

  const state = {
    payload: null,
    iso: null,
    offerable: [], // StockedBoxPayload.offerableDates() entries this page offers
    products: new Map(), // handle -> /products/<handle>.js body, or null
    removed: new Set(), // variant ids; kept across week changes
    quantities: new Map(), // variant ids -> customer-chosen quantity; kept across week changes
    weeksToShow: 1,
    runId: null, // stocked-recommendations run id, from ?r=, for order attribution
    testGroup: null, // ABC test group, from ?g=, for order attribution
    cartCount: 0,
    busy: false,
  };

  /* --------------------------------------------------------------- helpers */

  function money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function routes() {
    return window.routes || {};
  }

  function menuUrl() {
    return root.dataset.menuUrl || '/pages/menu';
  }

  function plural(n, one, many) {
    return `${n} ${n === 1 ? one : many}`;
  }

  function showFallback() {
    els.loading.hidden = true;
    els.main.hidden = true;
    els.fallback.hidden = false;
  }

  function showError(message) {
    els.error.textContent = message;
    els.error.hidden = false;
  }

  function clearError() {
    els.error.hidden = true;
    els.error.textContent = '';
  }

  // Mirrors delivery-date.js ajax(): fetch() only rejects on network failure, so
  // an unchecked 422 from the validation Function reads as a success. That is how
  // a frozen cart reached support as "the button does nothing". Deliberately NOT
  // StockedDelivery.setCartAttributes(), which swallows the rejection -- here a
  // refused attribute write must stop the add that follows it.
  function cartAjax(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (response.ok) return response.json().catch(() => null);
      return response
        .json()
        .catch(() => null)
        .then((data) => {
          const message = (data && (data.description || data.message)) || `Cart update failed (${response.status})`;
          const err = new Error(message);
          err.status = response.status;
          err.cartRejected = response.status === 422;
          throw err;
        });
    });
  }

  function fetchCart() {
    return fetch(`${routes().cart_url || '/cart'}.js`, { cache: 'no-store' }).then((r) => {
      if (!r.ok) throw new Error(`Could not read the cart (${r.status})`);
      return r.json();
    });
  }

  /* ------------------------------------------------------------------ data */

  // /products/<handle>.js is public and cacheable. One request per distinct handle
  // across EVERY week, in parallel, so a later date change needs no network.
  // A product that 404s (unpublished since the email went out) resolves to null.
  function loadProducts(payload) {
    const handles = new Set();
    Object.keys(payload.w).forEach((code) => payload.w[code].forEach((line) => handles.add(line[2])));
    return Promise.all(
      Array.from(handles).map((handle) =>
        fetch(`/products/${encodeURIComponent(handle)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((data) => state.products.set(handle, data))
      )
    );
  }

  function currentWeek() {
    return window.StockedDeliveryFormat.weekCodeOf(state.iso);
  }

  // Keeps only entries from the first `n` distinct week codes offerable carries
  // (in the order they already come in, which is chronological). Order-preserving
  // rather than a re-sort, so this stays correct if offerable's own ordering
  // logic ever changes.
  function limitToWeeks(offerable, n) {
    const fmt = window.StockedDeliveryFormat;
    const allowed = new Set();
    return offerable.filter((entry) => {
      const code = fmt.weekCodeOf(entry.iso);
      if (!allowed.has(code)) {
        if (allowed.size >= n) return false;
        allowed.add(code);
      }
      return true;
    });
  }

  // The quantity a row displays/submits at: the customer's own edit if they've
  // made one, otherwise the box's original quantity. Seeded lazily (on first
  // render of that variant) so it survives week switches the same way
  // state.removed does.
  function quantityFor(variantId, payloadQuantity) {
    if (!state.quantities.has(variantId)) state.quantities.set(variantId, payloadQuantity);
    return state.quantities.get(variantId);
  }

  // Display rows for the selected week. Lines whose product or variant no longer
  // exists drop out entirely; sold-out variants stay visible, so the box doesn't
  // silently differ from the one the email showed.
  function rows() {
    return (state.payload.w[currentWeek()] || [])
      .map(([variantId, payloadQuantity, handle]) => {
        const product = state.products.get(handle);
        const variant = product && (product.variants || []).find((v) => v.id === variantId);
        if (!variant) return null;
        const title = product.variants.length > 1 && variant.title && variant.title !== 'Default Title'
          ? `${product.title} – ${variant.title}`
          : product.title;
        return {
          variantId,
          recommendedQuantity: payloadQuantity,
          quantity: quantityFor(variantId, payloadQuantity),
          quantityRule: variant.quantity_rule || {},
          title,
          url: product.url || `/products/${handle}`,
          image: (variant.featured_image && variant.featured_image.src) || product.featured_image || '',
          price: variant.price,
          available: variant.available !== false,
          removed: state.removed.has(variantId),
          special: state.payload.s.indexOf(variantId) !== -1,
        };
      })
      .filter(Boolean);
  }

  function selectedDateLabel() {
    const entry = state.offerable.find((e) => e.iso === state.iso);
    return entry ? window.StockedDeliveryFormat.deliveryBarDateLabel(entry.y, entry.m, entry.d) : '';
  }

  function addableRows() {
    return rows().filter((row) => row.available && !row.removed);
  }

  /* ---------------------------------------------------------------- render */

  function renderDates(offerable) {
    const fmt = window.StockedDeliveryFormat;
    const delivery = window.StockedDelivery;
    els.weeks.textContent = '';

    const byWeek = new Map();
    offerable.forEach((entry) => {
      const code = fmt.weekCodeOf(entry.iso);
      if (!byWeek.has(code)) byWeek.set(code, []);
      byWeek.get(code).push(entry);
    });

    byWeek.forEach((entries, code) => {
      const group = document.createElement('div');
      group.className = 'your-box__week';

      // Weeks only get a heading when there is more than one: it is what tells the
      // customer that moving between them changes the box.
      if (byWeek.size > 1) {
        const label = document.createElement('p');
        label.className = 'your-box__week-label';
        label.textContent = delivery.windowLabelForWeek(code);
        group.appendChild(label);
      }

      const list = document.createElement('ul');
      list.className = 'your-box__date-list';
      list.setAttribute('role', 'list');

      entries.forEach((entry) => {
        const id = `YourBoxDate-${entry.iso}`;
        const item = document.createElement('li');
        item.className = 'your-box__date';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'your-box-date';
        input.id = id;
        input.value = entry.iso;
        input.checked = entry.iso === state.iso;
        input.addEventListener('change', () => {
          clearError();
          state.iso = entry.iso;
          renderItems();
        });

        const label = document.createElement('label');
        label.setAttribute('for', id);
        label.textContent = fmt.deliveryBarDateLabel(entry.y, entry.m, entry.d);

        item.appendChild(input);
        item.appendChild(label);
        list.appendChild(item);
      });

      group.appendChild(list);
      els.weeks.appendChild(group);
    });
  }

  function renderItems() {
    const all = rows();
    const addable = all.filter((row) => row.available && !row.removed);
    els.items.textContent = '';

    all.forEach((row) => {
      const node = els.template.content.cloneNode(true);
      const li = node.querySelector('li');
      const media = node.querySelector('[data-item-link]');
      const image = node.querySelector('[data-item-image]');
      const title = node.querySelector('[data-item-title]');
      const meta = node.querySelector('[data-item-meta]');
      const action = node.querySelector('[data-item-action]');
      const quantityWrapper = node.querySelector('[data-item-quantity]');
      const quantityInput = node.querySelector('[data-item-quantity-input]');

      media.href = row.url;
      title.href = row.url;
      title.textContent = row.title;
      if (row.image) {
        image.src = sizedImage(row.image, 240);
      } else {
        image.remove();
      }
      if (row.special) node.querySelector('[data-item-special]').hidden = false;
      node.querySelector('[data-item-price]').textContent = money(row.price * row.quantity);

      if (!row.available) {
        li.classList.add('your-box__item--unavailable');
        meta.textContent = 'Sold out this week';
        action.remove();
        quantityWrapper.remove();
      } else {
        meta.textContent = `${money(row.price)} each`;
        if (row.removed) li.classList.add('your-box__item--removed');
        action.textContent = row.removed ? 'Undo' : 'Remove';
        action.setAttribute('aria-label', `${row.removed ? 'Put back' : 'Remove'} ${row.title}`);
        action.addEventListener('click', () => {
          if (state.removed.has(row.variantId)) state.removed.delete(row.variantId);
          else state.removed.add(row.variantId);
          renderItems();
          // The row was rebuilt; keep focus on the same control for keyboard users.
          const again = els.items.querySelector(`[data-variant-id="${row.variantId}"] [data-item-action]`);
          if (again) again.focus();
        });

        // Nothing to adjust on a line the customer has taken out -- Undo brings
        // the stepper back with whatever quantity they'd last set.
        quantityWrapper.hidden = row.removed;
        quantityInput.value = row.quantity;
        quantityInput.min = row.quantityRule.min || 1;
        if (row.quantityRule.max != null) quantityInput.max = row.quantityRule.max;
        quantityInput.step = row.quantityRule.increment || 1;
        quantityInput.setAttribute('aria-label', `Quantity for ${row.title}`);
        quantityInput.addEventListener('change', () => {
          const min = parseInt(quantityInput.min, 10) || 1;
          const value = Math.max(min, parseInt(quantityInput.value, 10) || min);
          state.quantities.set(row.variantId, value);
          renderItems();
          const again = els.items.querySelector(`[data-variant-id="${row.variantId}"] [data-item-quantity-input]`);
          if (again) again.focus();
        });
      }

      li.dataset.variantId = row.variantId;
      els.items.appendChild(node);
    });

    els.count.textContent = plural(units(addable), 'pick', 'picks');
    els.total.textContent = money(addable.reduce((sum, row) => sum + row.price * row.quantity, 0));
    renderSummaryMeta(addable);
    setBusy(state.busy);
  }

  function units(rowList) {
    return rowList.reduce((sum, row) => sum + row.quantity, 0);
  }

  // "6 items · delivering Mon, Oct 26th". The "delivering" word is its own span
  // so the compact (bottom bar) summary can drop it for width.
  function renderSummaryMeta(addable) {
    const meta = els.summaryMeta;
    meta.textContent = `${plural(units(addable), 'item', 'items')} · `;
    const verb = document.createElement('span');
    verb.className = 'your-box__summary-verb';
    verb.textContent = 'delivering ';
    meta.appendChild(verb);
    meta.appendChild(document.createTextNode(selectedDateLabel()));
  }

  // Swaps a button's own label for `label` while busy, remembering the
  // original so it can come back unchanged. A button with a [data-box-cta-label]
  // child only has that child swapped, so its icon survives. Same shape as delivery-date.js's
  // setDialogBusy(): a stuck spinner-less button reads as "did my click even
  // register", which is worse than no feedback at all.
  function setButtonBusyLabel(button, busy, label) {
    const target = button.querySelector('[data-box-cta-label]') || button;
    if (busy) {
      if (!button.dataset.idleLabel) button.dataset.idleLabel = target.textContent;
      target.textContent = label;
    } else if (button.dataset.idleLabel) {
      target.textContent = button.dataset.idleLabel;
      delete button.dataset.idleLabel;
    }
  }

  function setBusy(busy, button) {
    state.busy = busy;
    const empty = addableRows().length === 0;
    [els.cta].forEach((el) => {
      el.disabled = busy || empty;
      const busyHere = busy && el === button;
      if (busyHere) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
      setButtonBusyLabel(el, busyHere, 'Adding…');
    });
    root.querySelectorAll('[data-item-action], [data-item-quantity-input], input[name="your-box-date"]').forEach((el) => {
      el.disabled = busy;
    });
  }

  // Puts the already-have-a-cart dialog away and undoes any busy label left
  // on its buttons from a submit that failed (a successful one navigates away
  // instead, so there's nothing left to reset).
  function resetCartDialog() {
    const dialog = els.dialog;
    dialog.classList.remove('is-active');
    dialog.hidden = true;
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(els.cta);
    dialog.querySelectorAll('[data-box-dialog-add], [data-box-dialog-replace], [data-box-dialog-cancel]').forEach((el) => {
      el.disabled = false;
      el.removeAttribute('aria-busy');
      setButtonBusyLabel(el, false, '');
    });
  }

  // Asks the customer to add-alongside or replace when they already have a
  // cart, instead of silently merging into or wiping out something unrelated.
  // Resolves 'add' | 'replace' | null (cancelled). Self-contained rather than
  // StockedDelivery's shared runDialog(): that one is built for a two-way
  // confirm/cancel, and this needs three outcomes.
  //
  // Choosing add/replace does NOT close the dialog -- it holds it open, busy,
  // with the chosen button's label swapped, for the whole submit() that
  // follows (same "hold" idea as runDialog's opts.hold). Closing it right away
  // left a stretch with the dialog gone, nothing on screen reacting, and the
  // page just sitting there until the redirect -- reading as broken, not busy.
  function promptCartChoice() {
    return new Promise((resolve) => {
      const dialog = els.dialog;
      const box = dialog.querySelector('[data-box-dialog-box]');
      const addBtn = dialog.querySelector('[data-box-dialog-add]');
      const replaceBtn = dialog.querySelector('[data-box-dialog-replace]');
      const cancelBtn = dialog.querySelector('[data-box-dialog-cancel]');
      let settled = false;

      function close(result) {
        if (settled) return;
        settled = true;
        dialog.classList.remove('is-active');
        dialog.hidden = true;
        dialog.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKeydown);
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(els.cta);
        resolve(result);
      }

      function hold(choice, button) {
        if (settled) return;
        settled = true;
        dialog.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKeydown);
        [addBtn, replaceBtn, cancelBtn].forEach((el) => { el.disabled = true; });
        button.setAttribute('aria-busy', 'true');
        setButtonBusyLabel(button, true, choice === 'add' ? 'Adding…' : 'Replacing…');
        resolve(choice);
      }

      function onClick(event) {
        if (event.target.closest('[data-box-dialog-add]')) hold('add', addBtn);
        else if (event.target.closest('[data-box-dialog-replace]')) hold('replace', replaceBtn);
        else if (event.target.closest('[data-box-dialog-cancel], [data-box-dialog-overlay]')) close(null);
      }

      function onKeydown(event) {
        if (event.key === 'Escape') close(null);
      }

      // hidden removed and is-active added on separate frames -- same as
      // StockedDelivery's runDialog() -- so the overlay's opacity transition
      // actually animates instead of snapping straight to visible.
      dialog.hidden = false;
      requestAnimationFrame(() => dialog.classList.add('is-active'));
      dialog.addEventListener('click', onClick);
      document.addEventListener('keydown', onKeydown);
      if (typeof window.trapFocus === 'function') window.trapFocus(box, addBtn);
      else addBtn.focus();
    });
  }

  /* ------------------------------------------------------------- cart build */

  // Empties the cart for "Replace my cart". /cart/clear.js goes through the
  // validation Function like every other write, so a frozen cart refuses it; in
  // that case abandon the token instead, as recoverCart() does, and confirm a
  // genuinely fresh, empty cart exists before anything is written to it.
  async function emptyCart() {
    try {
      await cartAjax(routes().cart_clear_url ? `${routes().cart_clear_url}.js` : '/cart/clear.js', {});
      return;
    } catch (err) {
      if (!err.cartRejected) throw err;
    }
    const before = await fetchCart();
    document.cookie = 'cart=; Max-Age=0; path=/';
    const fresh = await fetchCart();
    if (fresh.token === before.token || fresh.item_count > 0) {
      throw new Error("We couldn't clear your cart. Please remove its items and try again.");
    }
  }

  async function submit(mode, button) {
    const delivery = window.StockedDelivery;
    const lines = addableRows();
    if (state.busy || !lines.length) return;

    clearError();
    setBusy(true, button);
    let removedByReconcile = false;

    try {
      if (!delivery.findDate(state.iso)) {
        throw new Error('That delivery date just closed. Please choose another one.');
      }

      if (mode === 'replace') {
        await emptyCart();
      } else if (state.cartCount > 0) {
        // The existing cart must be writable, and must still be deliverable on the
        // box's date once the date moves -- otherwise the attribute write below
        // lands, and the add after it is refused by a cart that is now frozen.
        // Same reconcile-before-mutate path every other date commit uses.
        if (delivery.cartFrozenReason()) {
          setBusy(false);
          delivery.offerRecovery();
          return;
        }
        const result = await delivery.reconcileForDate(state.iso);
        removedByReconcile = result.removed;
        if (!result.proceed) {
          if (result.removed) location.reload();
          else setBusy(false);
          return;
        }
      }

      // Attributes FIRST, lines second. The validation Function evaluates the
      // PRE-change cart: dating the cart before the lines exist means it is never
      // in a state the Function refuses. Reversing these reintroduces the
      // frozen-cart bug.
      const selection = delivery.select(state.iso);
      if (!selection) throw new Error('That delivery date just closed. Please choose another one.');
      const attributes = Object.assign({}, delivery.toAttributes(selection), {
        recommendations_source: 'your-box',
        recommendations_week_code: currentWeek(),
      });
      if (state.runId) attributes.recommendations_run_id = state.runId;
      // Always written, '' included: an empty value deletes the attribute, so a
      // group left on the cart by an earlier link can't label this order.
      attributes.recommendations_test_group = state.testGroup || '';
      await cartAjax(routes().cart_update_url || '/cart/update.js', { attributes });

      await cartAjax(routes().cart_add_url || '/cart/add.js', {
        // Underscored, so it stays off the cart and checkout but shows on the
        // order in admin. A line without it, or above it, was added on top.
        items: lines.map((row) => ({
          id: row.variantId,
          quantity: row.quantity,
          properties: { _recommended_quantity: String(row.recommendedQuantity) },
        })),
      });

      // Cart lines land here, but the customer accepts/adjusts them on the menu,
      // not this page -- open with the drawer already up so what just got added
      // is the first thing they see.
      localStorage.setItem('openCartAfterRefresh', 'true');
      window.location.href = menuUrl();
    } catch (err) {
      console.error('your-box: add to cart failed', err);
      if (removedByReconcile) {
        // Lines already came out of the cart and the reconcile dialog is held
        // open; the page must resync rather than show a cart that no longer exists.
        window.alert(err.message);
        location.reload();
        return;
      }
      setBusy(false);
      showError(err.message);
      // A refusal may mean the cart was frozen before this page was opened. Offer
      // the existing escape hatch rather than leaving the customer stuck.
      if (err.cartRejected) delivery.offerRecovery();
    }
  }

  /* ----------------------------------------------------------------- start */

  async function start() {
    const boxes = window.StockedBoxPayload;
    const delivery = window.StockedDelivery;
    if (!boxes || !delivery || !window.StockedDeliveryFormat) return showFallback();

    const params = new URLSearchParams(window.location.search);
    const payload = boxes.decodeBox(params.get('b'));
    if (!payload) return showFallback();
    state.runId = params.get('r') || null;
    // Unsigned like the rest of the link, so only a bare group letter is kept.
    const group = params.get('g');
    state.testGroup = /^[A-Z]$/.test(group || '') ? group : null;

    const weeksToShow = parseInt(root.dataset.weeksToShow, 10);
    state.weeksToShow = weeksToShow > 0 ? weeksToShow : 1;
    const offerable = limitToWeeks(boxes.offerableDates(payload, delivery.selectableDates()), state.weeksToShow);
    const iso = boxes.initialDate(payload, offerable);
    // Every week in the link has closed: nothing here can be delivered any more.
    if (!iso) return showFallback();

    state.payload = payload;
    state.iso = iso;
    state.offerable = offerable;

    const [, cart] = await Promise.all([loadProducts(payload), fetchCart().catch(() => null)]);
    state.cartCount = cart ? cart.item_count : 0;

    // Every product in the link has been unpublished.
    const anyProduct = Object.keys(payload.w).some((code) =>
      payload.w[code].some((line) => state.products.get(line[2]))
    );
    if (!anyProduct) return showFallback();

    renderDates(offerable);
    renderItems();

    els.cta.addEventListener('click', async () => {
      if (state.busy) return;
      if (state.cartCount > 0) {
        const choice = await promptCartChoice();
        if (choice === 'add' || choice === 'replace') {
          await submit(choice, els.cta);
          // submit() only ever returns (rather than navigating away) when it
          // failed -- state.busy is back to false in that case, and the held
          // dialog needs putting away so the page's own error message reads
          // as the reason, not a stuck "Adding…" button.
          if (!state.busy) resetCartDialog();
        }
      } else {
        submit('add', els.cta);
      }
    });

    els.loading.hidden = true;
    els.main.hidden = false;
  }

  function run() {
    start().catch((err) => {
      console.error('your-box: failed to load', err);
      showFallback();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
